import { QueryClient, QueryFunction } from "@tanstack/react-query";

// API base resolution priority:
// 1. VITE_API_BASE_URL (build-time env, used for the Cloudflare deploy)
// 2. __PORT_5000__ replacement (legacy sandbox deploy)
// 3. Empty string (relative URLs — local dev with Vite proxy)
const VITE_API = (import.meta.env.VITE_API_BASE_URL as string | undefined) || "";
const PORT_REPLACED = "__PORT_5000__".startsWith("__") ? "" : "__PORT_5000__";
export const API_BASE = VITE_API || PORT_REPLACED;

// Cross-origin (absolute) API bases must NOT send credentials — the token in the
// URL is the auth, and browsers block wildcard CORS responses paired with
// `credentials: 'include'`. Same-origin / proxied bases keep credentials so the
// dashboard can pass through Cloudflare Access cookies.
const IS_CROSS_ORIGIN = /^https?:\/\//i.test(API_BASE);
const CREDENTIALS_MODE: RequestCredentials = IS_CROSS_ORIGIN ? "omit" : "include";

// Custom error that exposes the parsed JSON body so callers can read
// structured fields like { message, code, repayUrl } returned by the server.
export class ApiError extends Error {
  status: number;
  data: any;
  constructor(status: number, data: any, message: string) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = await res.text();
    let data: any = {};
    try { data = text ? JSON.parse(text) : {}; } catch { data = { message: text }; }
    const message = data?.message || text || res.statusText || `HTTP ${res.status}`;
    throw new ApiError(res.status, data, message);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(`${API_BASE}${url}`, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: CREDENTIALS_MODE,
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(`${API_BASE}${queryKey.join("/")}`, {
      credentials: CREDENTIALS_MODE,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
